//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
// import Typography from '@material-ui/core/Typography';
import Bookmark from '@material-ui/icons/Bookmark';
import Launch from '@material-ui/icons/Launch';
import { BookmarkBorderOutlined } from '@material-ui/icons';
import SimpleBar from 'simplebar-react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { nl2br } from '../../common/textHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => {
        const comingSoonStyles = {
            paddingLeft: 24,
            paddingRight: 24,
            marginRight: 10,
            marginBottom: 5,
            textTransform: 'none',
            [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 },
        };

        return {
            root: {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 0,
                // marginTop: 30,
                '& .MuiBox-root': {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                '& .Mui-selected': {
                    color: '#1D8A8B',
                },
                '& .MuiTabs-indicator': {
                    height: '4px',
                },
                '& .MuiTabs-flexContainer': {
                    borderBottom: '1px solid #F0F2F6',
                },
                '& .MuiTabs-flexContainer .MuiButtonBase-root': {
                    marginRight: '30px',
                    padding: 0,
                    minWidth: '3rem',
                },
            },
            tabPanelsContainer: {
                flex: 1,
                // overflow: 'hidden',
                paddingTop: '36px',
                // height: 250,
                // overflowY: 'scroll',
                // overflowX: 'hidden',
                // '&::-webkit-scrollbar-track': {
                //     borderRadius: '10px',
                //     background: 'none',
                // },
                // '&::-webkit-scrollbar': {
                //     width: '8px',
                //     backgroundColor: '#F5F5F5',
                // },
                // '&::-webkit-scrollbar-thumb': {
                //     borderRadius: '10px',
                //     backgroundColor: 'rgba(0,0,0,0.2)',
                // },
            },
            tabPanel: {
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            },
            tabPanelBox: {
                height: '100%',
                overflow: 'hidden',
            },
            tabTitle: {
                fontSize: '18px',
                color: '#000',
                marginBottom: 5,
                letterSpacing: 0,
                fontWeight: 600,
                textAlign: 'right',
                '& span:nth-of-type(1)': {
                    textTransform: 'capitalize !important',
                },
                [theme.breakpoints.down('sm')]: { fontSize: 15 },
            },
            buttonBox: {
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 20,
                [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
            },
            comingSoonButton: comingSoonStyles,
            filledButton: comingSoonStyles,
            applyNowButton: {
                paddingLeft: 24,
                paddingRight: 24,
                marginRight: 10,
                marginBottom: 5,
                textTransform: 'none',
                [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 },
            },
            saveJobButton: {
                paddingLeft: 24,
                paddingRight: 24,
                marginBottom: 5,
                textTransform: 'none',
                color: '#000',
                marginRight: 10,
                [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 },
            },
            applyOnCompanySiteButton: {
                paddingLeft: 24,
                paddingRight: 24,
                marginBottom: 5,
                textTransform: 'none',
                [theme.breakpoints.down('sm')]: { width: '100%' },
                [theme.breakpoints.only('md')]: { paddingLeft: 19, paddingRight: 19 },
            },
            content: {},
            paragraph: {
                wordBreak: 'break-word',
                marginBottom: '20px',
            },
            hidden: {
                display: 'none',
            },
        };
    });
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, value, changeTabHandler, props, bookmarked, applied, mdMatchesOnly } = params;

    const { applyJobOnClick, saveJobOnClick, applyOnCompanySiteOnClick } = props;

    const defns = {
        appBar: {
            position: 'static',
            style: { background: '#fff' },
            elevation: '0',
        },
        tabs: {
            value,
            onChange: changeTabHandler,
            'aria-label': 'simple tabs example',
        },

        comingSoonButton: {
            label: 'Coming Soon',
            color: 'primary',
            disabled: true,
            variant: 'contained',
            disableElevation: true,
        },
        filledButton: {
            label: 'Already Filled',
            color: 'primary',
            disabled: true,
            variant: 'contained',
            disableElevation: true,
        },
        applyNowButton: {
            label: applied ? 'Applied' : mdMatchesOnly ? 'Apply' : 'Apply Now',
            color: 'primary',
            disabled: applied,
            onClick: () => {
                if (!applied) applyJobOnClick();
            },
            variant: 'contained',
            disableElevation: true,
        },
        saveJobButton: {
            label: mdMatchesOnly ? `${bookmarked ? 'Saved' : 'Save'}` : `${bookmarked ? 'Saved' : 'Save'}  to My Jobs`,
            color: 'default',
            onClick: saveJobOnClick,
            startIcon: bookmarked ? <Bookmark style={{ color: '#FC821F', fontSize: '24px' }} /> : <BookmarkBorderOutlined style={{ fontSize: '24px' }} />,
            disableElevation: true,
        },
        applyOnCompanySiteButton: {
            label: 'Apply on Company’s Site',
            color: 'secondary',
            onClick: applyOnCompanySiteOnClick,
            endIcon: <Launch style={{ fontSize: '24px' }} />,
            disableElevation: true,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};
//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const TabPanel = props => {
    const classes = useStyles();
    const { children, value, index, ...otherProps } = props;

    const getDefns = () => {
        const { tabPanel: tabPanelCls, hidden: hiddenCls } = classes;
        const defns = {
            tabPanel: {
                id: `simple-tabpanel-${index}`,
                role: 'tabpanel',
                // TODO change this logic to classnames
                // style: { display: value !== index ? 'none' : 'block' },
                className: cx(tabPanelCls, { [hiddenCls]: value !== index }),
                'aria-labelledby': `simple-tab-${index}`,
            },
        };

        // inject classes
        setClassNameInDefns(defns, classes);

        return defns;
    };
    const defns = getDefns();

    return (
        <div {...defns.tabPanel} {...otherProps}>
            {value === index && children}
        </div>
    );
};

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

const getA11yProps = (index, cls) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
        className: cls,
    };
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SimpleTabs = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const { tabTitle: tabTitleCls } = classes;

    const appContext = useContext(AppContext);
    const { currentUser } = appContext;
    const bookmarked = currentUser?.bookmarkedJobs?.includes(props.uid); //uid is the job id
    const theme = useTheme();
    const mdMatchesOnly = useMediaQuery(theme.breakpoints.only('md'));

    // const layoutContext = useContext(LayoutContext);
    // const { isBrandPage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { jobDescription = '', benefitSummary = '', companyBio = '', jobApplicationUrl, appliedJobs, uid: jobId, comingSoon, filled } = props;
    const appliedDt = _get(appliedJobs, jobId);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [value, setValue] = React.useState(0);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const applied = !!appliedDt;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const Actions = ({ comingSoon, filled } = {}) => {
        return comingSoon ? (
            <div {...defns.buttonBox}>
                <AfitdButton {...defns.comingSoonButton} />
            </div>
        ) : filled ? (
            <div {...defns.buttonBox}>
                <AfitdButton {...defns.filledButton} />
            </div>
        ) : (
            <div {...defns.buttonBox}>
                <div>
                    <AfitdButton {...defns.applyNowButton} />
                    {jobApplicationUrl && <AfitdButton {...defns.saveJobButton} />}
                </div>

                {!jobApplicationUrl && <AfitdButton {...defns.saveJobButton} />}
                {jobApplicationUrl && <AfitdButton {...defns.applyOnCompanySiteButton} />}
            </div>
        );
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        value,
        changeTabHandler: handleChange,
        props,
        bookmarked,
        applied,
        mdMatchesOnly,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <AppBar {...defns.appBar}>
                <Tabs {...defns.tabs}>
                    <Tab label="Description" {...getA11yProps(0, tabTitleCls)} />
                    <Tab label="Benefits" {...getA11yProps(1, tabTitleCls)} />
                    <Tab label="Company" {...getA11yProps(2, tabTitleCls)} />
                </Tabs>
            </AppBar>
            <div {...defns.tabPanelsContainer}>
                <TabPanel value={value} index={0}>
                    <SimpleBar style={{ height: '100%' }}>
                        <Actions comingSoon={comingSoon} filled={filled} />
                        <div {...defns.content}>
                            <p {...defns.paragraph}>{nl2br(jobDescription)}</p>
                        </div>
                    </SimpleBar>
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <SimpleBar style={{ height: '100%' }}>
                        <Actions comingSoon={comingSoon} filled={filled} />
                        <div {...defns.content}>
                            <p {...defns.paragraph}>{nl2br(benefitSummary)}</p>
                        </div>
                    </SimpleBar>
                </TabPanel>
                <TabPanel value={value} index={2}>
                    <SimpleBar style={{ height: '100%' }}>
                        <Actions comingSoon={comingSoon} filled={filled} />
                        <div {...defns.content}>
                            <p {...defns.paragraph}>{nl2br(companyBio)}</p>
                        </div>
                    </SimpleBar>
                </TabPanel>
            </div>
        </div>
    );
};

export default SimpleTabs;
